import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

interface PollQuestionResult {
  questionId: string;
  text: string;
  type: string;
  options?: string[];
  optionCounts?: number[];
  textAnswers?: string[];
  totalResponses: number;
}

interface PollResultsData {
  pollId: string;
  title: string;
  totalResponses: number;
  questions: PollQuestionResult[];
}

interface UsePollResultsReturn {
  results: PollResultsData | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function usePollResults(pollId: string | undefined): UsePollResultsReturn {
  const [results, setResults] = useState<PollResultsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchResults = useCallback(async () => {
    if (!pollId) return;
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const data = await api.getPollResults(pollId);
      if (fetchIdRef.current !== id) return;
      setResults(data);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load results');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [pollId]);

  const refresh = useCallback(() => {
    fetchResults();
  }, [fetchResults]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { results, loading, error, refresh };
}
